import { useEffect, useMemo, useState } from "react";
import { Alert, Button, Card, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import {
  Chart as ChartJS,
  ArcElement,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar, Doughnut } from "react-chartjs-2";
import API from "../../services/api";

ChartJS.register(ArcElement, BarElement, CategoryScale, LinearScale, Tooltip, Legend);

const normalizeRows = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.rows)) return data.rows;
  if (Array.isArray(data?.results)) return data.results;
  if (Array.isArray(data?.data)) return data.data;
  return [];
};

const MODULES = [
  { title: "Purchase Requests", path: "/employee/purchase/requests", endpoint: "/employee/purchase/requests", perm: "purchase:request:create" },
  { title: "Purchase Orders", path: "/employee/purchase/orders", endpoint: "/employee/purchase/orders", perm: "purchase:order:create" },
  { title: "Vendor Master", path: "/employee/purchase/vendor-master", endpoint: "/employee/purchase/vendor-master", perm: "vendor-master:edit" },
  { title: "Rate Comparisons", path: "/employee/purchase/rate-comparisons", endpoint: "/employee/purchase/rate-comparisons", perm: "purchase:rate-comparison:view" },
  { title: "Material Availability", path: "/employee/purchase/material-availability", endpoint: "/employee/purchase/material-availability", perm: "material:availability:view" },
];

const today = () => new Date().toISOString().slice(0, 10);

export default function PurchaseDepartmentDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [perms, setPerms] = useState([]);
  const [counts, setCounts] = useState({});
  const [pendingCounts, setPendingCounts] = useState({});
  const [vendorsCount, setVendorsCount] = useState(0);
  const [attendanceStatus, setAttendanceStatus] = useState("Not Marked");
  const [idStatus, setIdStatus] = useState("Not Generated");
  const [lastSync, setLastSync] = useState(null);

  const visibleModules = useMemo(() => {
    const set = new Set(perms || []);
    return MODULES.filter((m) => set.has(m.perm));
  }, [perms]);

  const load = async () => {
    setErr("");
    try {
      const meRes = await API.get("/auth/me");
      const nextPerms = meRes.data?.employeeRole?.permissions || [];
      const permsList = Array.isArray(nextPerms) ? nextPerms : [];
      setPerms(permsList);

      const attendanceReq = API.get("/attendance/today").catch(() => ({ data: null }));
      const idReq = API.get("/employee/my-idcard").catch(() => ({ data: null }));
      const vendorsReq = permsList.includes("vendors:view")
        ? API.get(`/employee/vendors?date=${today()}`).catch(() => ({ data: [] }))
        : Promise.resolve({ data: [] });

      const moduleReqs = MODULES.map((m) =>
        API.get(m.endpoint)
          .then((res) => {
            const rows = normalizeRows(res.data);
            const pending = rows.filter((r) => String(r?.status || r?.approvalStatus || "").toLowerCase().includes("pending")).length;
            return { key: m.path, count: rows.length, pending };
          })
          .catch(() => ({ key: m.path, count: 0, pending: 0 }))
      );

      const [attendanceRes, idRes, vendorsRes, ...moduleRes] = await Promise.all([
        attendanceReq,
        idReq,
        vendorsReq,
        ...moduleReqs,
      ]);

      setAttendanceStatus(attendanceRes?.data?.status || "Not Marked");
      setIdStatus(idRes?.data?.idCardStatus || "Not Generated");
      setVendorsCount(Array.isArray(vendorsRes?.data) ? vendorsRes.data.length : 0);
      setCounts(moduleRes.reduce((acc, row) => ({ ...acc, [row.key]: row.count }), {}));
      setPendingCounts(moduleRes.reduce((acc, row) => ({ ...acc, [row.key]: row.pending }), {}));
      setLastSync(new Date());
    } catch (e) {
      setErr(e?.response?.data?.message || "Failed to load purchase dashboard.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const timer = window.setInterval(load, 30000);
    return () => window.clearInterval(timer);
  }, []);

  const doughnutData = useMemo(() => ({
    labels: visibleModules.map((m) => m.title),
    datasets: [{
      data: visibleModules.map((m) => counts[m.path] || 0),
      backgroundColor: ["#2563eb", "#16a34a", "#f59e0b", "#9333ea", "#0ea5e9"],
      borderWidth: 1,
    }],
  }), [visibleModules, counts]);

  const pendingBarData = useMemo(() => ({
    labels: visibleModules.map((m) => m.title),
    datasets: [{
      label: "Pending",
      data: visibleModules.map((m) => pendingCounts[m.path] || 0),
      backgroundColor: "#ef4444",
      borderRadius: 8,
    }],
  }), [visibleModules, pendingCounts]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <Spinner animation="border" />
      </div>
    );
  }

  return (
    <div className="accounts-page">
      <div className="accounts-header">
        <div>
          <h4 className="mb-1">Plant Purchase Dashboard</h4>
          <div className="accounts-subtitle">
            Requests, orders, vendor master, rates aur material availability ka live snapshot.
          </div>
        </div>
        <div className="d-flex gap-2">
          <Button variant="outline-secondary" onClick={load}>Refresh</Button>
          <Button variant="success" onClick={() => navigate("/employee/attendance")}>Attendance</Button>
          <Button variant="dark" onClick={() => navigate("/employee/my-idcard")}>ID Card</Button>
        </div>
      </div>

      {err ? <Alert variant="danger">{err}</Alert> : null}

      <div className="row g-3 mb-3">
        <div className="col-md-3">
          <Card className="shadow-sm"><Card.Body><div className="text-muted small">Today Attendance</div><div className="fw-bold fs-5">{attendanceStatus}</div></Card.Body></Card>
        </div>
        <div className="col-md-3">
          <Card className="shadow-sm"><Card.Body><div className="text-muted small">ID Card Status</div><div className="fw-bold fs-5">{idStatus}</div></Card.Body></Card>
        </div>
        <div className="col-md-3">
          <Card className="shadow-sm"><Card.Body><div className="text-muted small">Assigned Vendors</div><div className="fw-bold fs-5">{vendorsCount}</div></Card.Body></Card>
        </div>
        <div className="col-md-3">
          <Card className="shadow-sm"><Card.Body><div className="text-muted small">Last Sync</div><div className="fw-bold">{lastSync ? lastSync.toLocaleTimeString() : "—"}</div></Card.Body></Card>
        </div>
      </div>

      <div className="row g-3 mb-3">
        <div className="col-lg-6">
          <Card className="shadow-sm h-100">
            <Card.Header className="fw-semibold">Module Records Split</Card.Header>
            <Card.Body style={{ minHeight: 280 }}>
              {visibleModules.length ? (
                <Doughnut data={doughnutData} options={{ maintainAspectRatio: false }} />
              ) : (
                <Alert variant="warning" className="mb-0">No purchase module permissions assigned.</Alert>
              )}
            </Card.Body>
          </Card>
        </div>
        <div className="col-lg-6">
          <Card className="shadow-sm h-100">
            <Card.Header className="fw-semibold">Pending Items</Card.Header>
            <Card.Body style={{ minHeight: 280 }}>
              {visibleModules.length ? (
                <Bar
                  data={pendingBarData}
                  options={{
                    maintainAspectRatio: false,
                    plugins: { legend: { display: false } },
                    scales: { y: { beginAtZero: true, ticks: { precision: 0 } } },
                  }}
                />
              ) : (
                <Alert variant="warning" className="mb-0">No chart data available.</Alert>
              )}
            </Card.Body>
          </Card>
        </div>
      </div>

      <div className="row g-3">
        {visibleModules.map((module) => (
          <div className="col-md-4 col-lg-3" key={module.path}>
            <Card className="shadow-sm h-100">
              <Card.Body className="d-flex flex-column">
                <div className="text-muted small mb-1">Records</div>
                <div className="fw-bold fs-4">{counts[module.path] ?? 0}</div>
                <div className="text-danger small mb-2">Pending: {pendingCounts[module.path] ?? 0}</div>
                <div className="fw-semibold mb-3">{module.title}</div>
                <Button className="mt-auto" variant="outline-primary" onClick={() => navigate(module.path)}>
                  Open
                </Button>
              </Card.Body>
            </Card>
          </div>
        ))}
      </div>
    </div>
  );
}
